import { useCallback, useEffect, useRef, useState } from 'react';

import { legendContainer } from './ChartLegend.sty.js';
import { ChartLegendGrid } from './components/ChartLegendGrid.js';
import { ChartVirtualizedLegendGrid } from './components/ChartVirtualizedLegendGrid.js';
import { LegendGridSettingsContext } from './context/LegendGridSettingsContext.js';
import { useLegendInteractions } from './hooks/useLegendInteractions.js';
import { LegendItem } from './types/legend.js';
import { buildGridSettings } from './utils/legend-grid.utils.js';
import { VisuallyHidden } from '../../../../../core/components/visually-hidden/VisuallyHidden.js';
import { StylingProps } from '../../../../../core/types/styling-props.js';
import type { TruncationMode } from '../../../../../typography/text-ellipsis/TextEllipsis.js';
import { DEFAULT_TRUNCATION_MODE, ORIENTATION } from '../../../constants.js';
import { ChartLegendOrientation } from '../../../types/legend.js';
import type { SeriesActions } from '../../../types/series-actions-template.js';
import { LegendContext } from '../shared/context/Legend.context.js';
import { useLegendSize } from '../shared/context/LegendSizeContext.js';

/** How long an announcement node stays mounted before being removed from the live region. */
const ANNOUNCE_CLEANUP_MS = 1000;

interface Announcement {
  id: number;
  message: string;
}

/**
 * @internal
 */
export interface ChartLegendProps extends StylingProps {
  /** The series of the legend */
  items: LegendItem[];
  /** Chart orientation
   ** @defaultValue 'horizontal' */
  orientation?: ChartLegendOrientation;
  /** Sometimes no legend interactions are allowed (while tooltip is pinned) */
  interactionsDisabled?: boolean;
  /** Called when the user hovers over a legend item */
  onHover?: (hoveredSeries?: LegendItem) => void;
  /** Called when the user click the legend item */
  onClick?: (visible: LegendItem[]) => void;
  /** Called when the user performs a legend action */
  onSeriesAction?: SeriesActions<LegendItem>;
  /**
   * Truncation mode to use (start, middle, end)
   * Applied to all the parts that truncate text.
   * @defaultValue 'middle'
   */
  truncationMode?: TruncationMode;
}

/**
 * @internal
 */
export const ChartLegend = (props: ChartLegendProps) => {
  const {
    items,
    orientation = ORIENTATION.Horizontal,
    onHover,
    onClick,
    onSeriesAction,
    interactionsDisabled = false,
    truncationMode = DEFAULT_TRUNCATION_MODE,
    style,
  } = props;

  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useLegendSize();
  const { onItemHover, onLegendIconClick, onLegendNameClick, hiddenItems } =
    useLegendInteractions(items, { onClick, onHover });

  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const nextAnnouncementId = useRef(0);
  const cleanupTimeouts = useRef(new Set<ReturnType<typeof setTimeout>>());

  // Each announcement is appended as a new child node rather than overwriting a
  // single text node. Screen readers (VoiceOver in particular) cache the last
  // spoken value and won't re-announce when a live region is merely cleared and
  // reset to the same string; inserting a fresh node reliably triggers a new
  // announcement even for repeated identical messages (e.g. "Copied"). Nodes are
  // removed shortly after so the region doesn't accumulate stale content.
  const announce = useCallback((message: string) => {
    const id = nextAnnouncementId.current++;
    setAnnouncements((current) => [...current, { id, message }]);

    const timeout = setTimeout(() => {
      setAnnouncements((current) =>
        current.filter((announcement) => announcement.id !== id),
      );
      cleanupTimeouts.current.delete(timeout);
    }, ANNOUNCE_CLEANUP_MS);
    cleanupTimeouts.current.add(timeout);
  }, []);

  useEffect(() => {
    const timeouts = cleanupTimeouts.current;
    return () => {
      timeouts.forEach(clearTimeout);
      timeouts.clear();
    };
  }, []);

  const gridSettings = buildGridSettings(items, width, height);

  return (
    <div
      ref={ref}
      data-testid="chart-legend"
      className={legendContainer}
      style={{
        height,
        width,
        flexDirection: orientation === ORIENTATION.Vertical ? 'row' : 'column',
        ...style,
      }}
    >
      <VisuallyHidden>
        <div role="status" aria-atomic="false">
          {announcements.map((announcement) => (
            <div key={announcement.id}>{announcement.message}</div>
          ))}
        </div>
      </VisuallyHidden>
      <LegendContext.Provider
        value={{
          hiddenItems,
          truncationMode,
          onSeriesAction,
          onLegendIconClick,
          onItemHover,
          onLegendNameClick,
          interactionsDisabled,
          onAnnounce: announce,
        }}
      >
        <LegendGridSettingsContext.Provider value={gridSettings}>
          {gridSettings.hasVirtual ? (
            <ChartVirtualizedLegendGrid
              items={items}
              orientation={orientation}
            />
          ) : (
            <ChartLegendGrid items={items} orientation={orientation} />
          )}
        </LegendGridSettingsContext.Provider>
      </LegendContext.Provider>
    </div>
  );
};

ChartLegend['displayName'] = 'ChartLegend';
